import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { listAssets, uploadAsset } from '@/services/assets';
import { AssetBinaryImage } from '@/components/domain/AssetBinaryPreview';
import type { Asset } from '@/types/api';

const ACCEPT_IMAGE = 'image/jpeg,image/png,image/webp,image/gif';
const ACCEPT_VIDEO = 'video/mp4,video/webm,video/quicktime';
const ACCEPT_ALL = `${ACCEPT_IMAGE},${ACCEPT_VIDEO}`;

const TABS = ['My Assets', 'Upload', 'Web URL'] as const;
type Tab = (typeof TABS)[number];

function fileMatchesType(file: File, allowed: 'image' | 'video') {
  return allowed === 'image' ? file.type.startsWith('image/') : file.type.startsWith('video/');
}

function blobMatchesType(blob: Blob, ct: string, allowed?: 'image' | 'video') {
  if (!allowed) return true;
  const mime = (blob.type || ct).split(';')[0].toLowerCase();
  return allowed === 'image' ? mime.startsWith('image/') : mime.startsWith('video/');
}

function nameFromUrl(url: string): string {
  try {
    const path = new URL(url).pathname;
    return path.split('/').filter(Boolean).pop() ?? 'asset';
  } catch {
    return 'asset';
  }
}

interface InsertAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (assetId: number) => void;
  onAssetCreated?: (asset: Asset) => void;
  allowedAssetType?: 'image' | 'video';
}

export default function InsertAssetModal({
  isOpen,
  onClose,
  onSelect,
  onAssetCreated,
  allowedAssetType,
}: InsertAssetModalProps) {
  const [tab, setTab] = useState<Tab>('My Assets');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [error, setError] = useState('');

  // Upload tab
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploading, setUploading] = useState(false);

  // URL tab
  const [webUrl, setWebUrl] = useState('');
  const [webLoading, setWebLoading] = useState(false);

  const fetchAssets = useCallback(() => {
    setLoadingAssets(true);
    setError('');
    listAssets({ limit: 200 })
      .then((page) => setAssets(page.items.filter((a) => a.id != null)))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load assets'))
      .finally(() => setLoadingAssets(false));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(null);
    setSearch('');
    setError('');
    setTab('My Assets');
    fetchAssets();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, fetchAssets, onClose]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = q ? assets.filter((a) => a.name.toLowerCase().includes(q)) : assets;
    if (allowedAssetType) list = list.filter((a) => a.asset_type === allowedAssetType);
    return list;
  }, [assets, search, allowedAssetType]);

  function handleConfirm() {
    if (selected != null && onSelect) onSelect(selected);
    onClose();
    setSelected(null);
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadFile) return;
    if (allowedAssetType && !fileMatchesType(uploadFile, allowedAssetType)) {
      setError(`Please select a ${allowedAssetType} file.`);
      return;
    }
    setUploading(true);
    setError('');
    try {
      const asset = await uploadAsset({ file: uploadFile, name: uploadName.trim() || undefined });
      if (asset.id != null) {
        onAssetCreated?.(asset);
        setAssets((prev) => [asset, ...prev.filter((a) => a.id !== asset.id)]);
        setSelected(asset.id);
        setTab('My Assets');
      }
      setUploadFile(null);
      setUploadName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function handleWebUrl(e: React.FormEvent) {
    e.preventDefault();
    const url = webUrl.trim();
    if (!url) return;
    setWebLoading(true);
    setError('');
    try {
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const ct = res.headers.get('content-type') ?? '';
      const blob = await res.blob();
      if (!blobMatchesType(blob, ct, allowedAssetType)) {
        throw new Error(
          allowedAssetType ? `URL isn't a ${allowedAssetType}.` : 'Unsupported media type.',
        );
      }
      const name = nameFromUrl(url);
      const file = new File([blob], name, { type: blob.type || ct.split(';')[0] });
      const asset = await uploadAsset({ file });
      if (asset.id != null) {
        onAssetCreated?.(asset);
        setAssets((prev) => [asset, ...prev.filter((a) => a.id !== asset.id)]);
        setSelected(asset.id);
        setTab('My Assets');
      }
      setWebUrl('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not fetch URL');
    } finally {
      setWebLoading(false);
    }
  }

  if (!isOpen) return null;

  const modalContent = (
    <div className="aspectly:fixed aspectly:inset-0 aspectly:z-[9999] aspectly:flex aspectly:items-center aspectly:justify-center aspectly:p-4">
      {/* Backdrop */}
      <div className="aspectly:absolute aspectly:inset-0 aspectly:bg-on-surface/30 aspectly:backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="aspectly:relative aspectly:z-10 aspectly:w-full aspectly:max-w-4xl aspectly:bg-surface-container-lowest aspectly:rounded-2xl aspectly:shadow-2xl aspectly:flex aspectly:flex-col aspectly:max-h-[90vh] aspectly:overflow-hidden aspectly:border aspectly:border-outline-variant/10">
        {/* Header */}
        <div className="aspectly:flex aspectly:items-center aspectly:justify-between aspectly:px-6 aspectly:py-4 aspectly:border-b aspectly:border-outline-variant/10">
          <div className="aspectly:flex aspectly:items-center aspectly:gap-6 aspectly:flex-1">
            <h2 className="aspectly:font-headline aspectly:font-bold aspectly:text-on-surface aspectly:text-lg aspectly:whitespace-nowrap">
              Insert Asset
              {allowedAssetType && (
                <span className="aspectly:ml-2 aspectly:text-sm aspectly:font-normal aspectly:text-on-surface-variant">
                  ({allowedAssetType} only)
                </span>
              )}
            </h2>
            {tab === 'My Assets' && (
              <div className="aspectly:relative aspectly:w-full aspectly:max-w-xs">
                <span className="material-symbols-outlined aspectly:absolute aspectly:left-3 aspectly:top-1/2 aspectly:-translate-y-1/2 aspectly:text-on-surface-variant aspectly:text-[18px]">
                  search
                </span>
                <input
                  className="aspectly:w-full aspectly:pl-9 aspectly:pr-4 aspectly:py-2 aspectly:text-sm aspectly:bg-surface-container-low aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-lg aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/30 aspectly:text-on-surface aspectly:placeholder:text-on-surface-variant/50"
                  placeholder="Search assets…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="aspectly:ml-4 aspectly:w-9 aspectly:h-9 aspectly:flex aspectly:items-center aspectly:justify-center aspectly:rounded-full aspectly:hover:bg-surface-container aspectly:text-on-surface-variant aspectly:hover:text-on-surface aspectly:transition-colors aspectly:cursor-pointer"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="aspectly:flex aspectly:gap-8 aspectly:px-6 aspectly:border-b aspectly:border-outline-variant/10 aspectly:bg-surface-container-low/60">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`aspectly:py-4 aspectly:text-sm aspectly:font-bold aspectly:transition-colors aspectly:cursor-pointer ${
                tab === t
                  ? 'aspectly:text-primary aspectly:border-b-2 aspectly:border-primary' : 'aspectly:text-on-surface-variant aspectly:hover:text-on-surface'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="aspectly:flex-1 aspectly:overflow-y-auto aspectly:p-6">
          {error && (
            <div className="aspectly:mb-4 aspectly:p-3 aspectly:rounded-lg aspectly:bg-error-container aspectly:text-on-error-container aspectly:text-sm">
              {error}
            </div>
          )}

          {/* My Assets */}
          {tab === 'My Assets' &&
            (loadingAssets ? (
              <div className="aspectly:flex aspectly:justify-center aspectly:py-16">
                <span className="material-symbols-outlined aspectly:animate-spin aspectly:text-primary aspectly:text-4xl">
                  progress_activity
                </span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="aspectly:py-16 aspectly:text-center">
                <span className="material-symbols-outlined aspectly:text-5xl aspectly:text-primary/30">
                  folder_open
                </span>
                <p className="aspectly:mt-4 aspectly:text-on-surface-variant aspectly:text-sm">
                  {assets.length === 0 ? 'No assets yet.' : 'No matching assets.'}
                </p>
              </div>
            ) : (
              <div className="aspectly:grid aspectly:grid-cols-2 aspectly:md:grid-cols-4 aspectly:lg:grid-cols-5 aspectly:gap-4">
                {filtered.map((asset) => (
                  <div
                    key={asset.id}
                    onClick={() => setSelected(asset.id!)}
                    className={`aspectly:group aspectly:relative aspectly:aspect-square aspectly:bg-surface-container aspectly:rounded-xl aspectly:overflow-hidden aspectly:cursor-pointer aspectly:transition-all ${
                      selected === asset.id
                        ? 'aspectly:ring-2 aspectly:ring-primary aspectly:border-2 aspectly:border-primary' : 'aspectly:border aspectly:border-outline-variant/20 aspectly:hover:border-primary/40'
                    }`}
                  >
                    {asset.asset_type === 'video' && !asset.thumbnail_url ? (
                      <div className="aspectly:w-full aspectly:h-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface-container-high">
                        <span className="material-symbols-outlined aspectly:text-on-surface-variant/50 aspectly:text-3xl">
                          movie
                        </span>
                      </div>
                    ) : (
                      <div className="aspectly:w-full aspectly:h-full aspectly:opacity-90 aspectly:group-hover:opacity-100 aspectly:transition-opacity">
                        <AssetBinaryImage
                          assetId={asset.id!}
                          endpoint={asset.thumbnail_url ? 'thumbnail' : 'content'}
                          remoteUrlHint={asset.thumbnail_url ?? asset.uri}
                          alt={asset.alt_text}
                          className="aspectly:w-full aspectly:h-full aspectly:object-cover"
                          loadingClassName="aspectly:w-full aspectly:h-full aspectly:flex aspectly:items-center aspectly:justify-center aspectly:bg-surface-container-high"
                        />
                      </div>
                    )}
                    {/* Name overlay */}
                    <div className="aspectly:absolute aspectly:inset-x-0 aspectly:bottom-0 aspectly:bg-linear-to-t aspectly:from-black/70 aspectly:p-2 aspectly:opacity-0 aspectly:group-hover:opacity-100 aspectly:transition-opacity">
                      <p className="aspectly:text-[9px] aspectly:font-bold aspectly:text-white aspectly:truncate">{asset.name}</p>
                    </div>
                    {/* Selected check */}
                    {selected === asset.id && (
                      <div className="aspectly:absolute aspectly:top-1.5 aspectly:right-1.5 aspectly:bg-primary aspectly:text-on-primary aspectly:rounded-full aspectly:p-0.5">
                        <span className="material-symbols-outlined aspectly:text-[13px]">check</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

          {/* Upload */}
          {tab === 'Upload' && (
            <form onSubmit={handleUpload} className="aspectly:max-w-md aspectly:space-y-4">
              <div>
                <label className="aspectly:block aspectly:text-xs aspectly:font-semibold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-wider aspectly:mb-2">
                  File
                </label>
                <input
                  type="file"
                  accept={
                    allowedAssetType === 'image'
                      ? ACCEPT_IMAGE
                      : allowedAssetType === 'video'
                        ? ACCEPT_VIDEO
                        : ACCEPT_ALL
                  }
                  onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                  className="aspectly:block aspectly:w-full aspectly:text-sm aspectly:text-on-surface-variant aspectly:file:mr-4 aspectly:file:py-2 aspectly:file:px-4 aspectly:file:rounded-lg aspectly:file:border-0 aspectly:file:bg-primary-container aspectly:file:text-primary aspectly:file:font-bold aspectly:file:cursor-pointer aspectly:hover:file:bg-primary-container/80"
                />
                {uploadFile && (
                  <p className="aspectly:mt-1 aspectly:text-xs aspectly:text-on-surface-variant aspectly:truncate">{uploadFile.name}</p>
                )}
              </div>
              <div>
                <label className="aspectly:block aspectly:text-xs aspectly:font-semibold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-wider aspectly:mb-2">
                  Display Name (optional)
                </label>
                <input
                  className="aspectly:w-full aspectly:px-3 aspectly:py-2.5 aspectly:bg-surface-container-low aspectly:text-sm aspectly:text-on-surface aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-lg aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/30"
                  placeholder="My asset name"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={!uploadFile || uploading}
                className="aspectly:w-full aspectly:py-3 aspectly:bg-primary aspectly:text-on-primary aspectly:rounded-xl aspectly:font-bold aspectly:text-sm aspectly:disabled:opacity-50 aspectly:cursor-pointer aspectly:hover:bg-primary-dim aspectly:transition-all"
              >
                {uploading ? 'Uploading…' : 'Upload & Insert'}
              </button>
            </form>
          )}

          {/* Web URL */}
          {tab === 'Web URL' && (
            <form onSubmit={handleWebUrl} className="aspectly:max-w-md aspectly:space-y-4">
              <p className="aspectly:text-xs aspectly:text-on-surface-variant">
                Paste a public URL to an image or video. It will be fetched and saved to your asset
                library.
              </p>
              <div>
                <label className="aspectly:block aspectly:text-xs aspectly:font-semibold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-wider aspectly:mb-2">
                  URL
                </label>
                <input
                  type="url"
                  required
                  className="aspectly:w-full aspectly:px-3 aspectly:py-2.5 aspectly:bg-surface-container-low aspectly:text-sm aspectly:text-on-surface aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-lg aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/30"
                  placeholder="https://example.com/image.jpg"
                  value={webUrl}
                  onChange={(e) => setWebUrl(e.target.value)}
                />
              </div>
              <button
                type="submit"
                disabled={!webUrl.trim() || webLoading}
                className="aspectly:w-full aspectly:py-3 aspectly:bg-primary aspectly:text-on-primary aspectly:rounded-xl aspectly:font-bold aspectly:text-sm aspectly:disabled:opacity-50 aspectly:cursor-pointer aspectly:hover:bg-primary-dim aspectly:transition-all"
              >
                {webLoading ? 'Fetching…' : 'Add from URL'}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="aspectly:px-6 aspectly:py-4 aspectly:border-t aspectly:border-outline-variant/10 aspectly:flex aspectly:items-center aspectly:justify-between aspectly:bg-surface-container-low/40">
          <p className="aspectly:text-xs aspectly:text-on-surface-variant">
            {tab === 'My Assets'
              ? `${filtered.length} asset${filtered.length !== 1 ? 's' : ''}`
              : '\u00a0'}
          </p>
          <div className="aspectly:flex aspectly:gap-3">
            <button
              onClick={onClose}
              className="aspectly:px-4 aspectly:py-2 aspectly:rounded-xl aspectly:text-sm aspectly:font-bold aspectly:text-on-surface-variant aspectly:hover:text-on-surface aspectly:hover:bg-surface-container aspectly:transition-colors aspectly:cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected == null || tab !== 'My Assets'}
              className="aspectly:px-5 aspectly:py-2 aspectly:rounded-xl aspectly:bg-primary aspectly:text-on-primary aspectly:text-sm aspectly:font-bold aspectly:disabled:opacity-40 aspectly:cursor-pointer aspectly:hover:bg-primary-dim aspectly:transition-all"
            >
              Select Asset
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
